import React from "react";
import { Link } from "react-router-dom";
import { has, sortBy } from "lodash";
import { TableContainer } from "../../components/Table";
import { Flex } from "../../components/Flex";
import { FilledLabel, Label, Paragraph, Title } from "../../components/Text";
import { Scrollbar } from "../../components/Scrollbar";
import { formatDate } from "../../utils";
import { parseDescription, renderFieldContent } from "../../components/Layout";
import { Badge } from "../../components/Circle";
import { InfoIcon } from "../../icons/InfoIcon";
import { FormTooltip } from "../../components/Tooltip";

const MainDashboardContent = ({ data={}, loading, pathname, search }) => {
    const fields = [
        { label: "Version", attr: "version" },
        { label: "System Architecture", attr: "sysarch", render: (d) => (
            <Flex gap={{ right: 8 }}>
                {Array.isArray(d)
                    ? d.map(sysarch => (
                        <FilledLabel key={sysarch}>{sysarch}</FilledLabel>
                    ))
                    : d
                }
            </Flex>
        )},
        { attr: "description", render: (d) => <Flex direction="column" gap={{ bottom: 2 }}>
        {typeof d === "string"
            ? parseDescription(d)
            : d}
        </Flex>}
    ];

    const Loading = ({ keyPrefix }) => (
        <Flex direction="column" gap={{bottom: 12 }} padding={{ right: "16px" }}>
            {Array(10).fill(null).map((val, idx) => (
                <Title key={`${keyPrefix}-loading-line${idx}`} h6 isLoading width="100%" />
            ))}
        </Flex>
    )

    return(
        <Flex flex={1} gap={{ right: 24 }}>
            <Flex direction="column" flex={1} gap={{ bottom: 12 }}>
                <Title h4>Recently released packages</Title>
                <TableContainer padding="32px 12px 32px 32px">
                    <Scrollbar>
                        {loading
                            ? <Loading keyPrefix={"recent"} />
                            : Object.keys(data).includes("recent") && Object.keys(data['recent']).length
                                ? <Flex direction="column" gap={{ bottom: 12 }} padding={{ right: "17px" }}>
                                    {sortBy(Object.values(data["recent"]), [ "releaseDate" ]).reverse().map((pkg, idx) => (
                                        has(pkg, "packageNumber")
                                        ? <Flex key={pkg.packageNumber} direction="column" gap={{ bottom: 8 }} padding={{ top: "12px" }} style={{ borderTop: idx > 0 && "1px solid var(--sp-light-grey-blue-color)"}}>
                                            <span><FilledLabel textTransform="capitalize" fill="var(--sp-success-light-color)" color="#FFF">{formatDate(pkg.releaseDate, "MMMM DD, YYYY")}</FilledLabel></span>
                                            <Title><Link to={{ pathname: `/packages/${pkg.packageNumber}`, state: { from: pathname + search } }}>{pkg.name}</Link></Title>
                                            {renderFieldContent(fields, pkg)}
                                        </Flex>
                                        : null
                                    ))}
                                </Flex>
                                : <Flex flex={1} justify="center">
                                    <Label bold color="#D7E3F7" size="32px">No packages available</Label>
                                </Flex>}
                    </Scrollbar>
                </TableContainer>
            </Flex>
            <Flex direction="column" flex={1} gap={{ bottom: 12 }}>
                <Title h4>Upcoming releases</Title>
                <TableContainer>
                    <Scrollbar>
                    {loading
                        ? <Loading keyPrefix="future" />
                        : Object.keys(data).includes("future") && Object.keys(data['future']).length
                            ? <Flex direction="column" gap={{ bottom: 12 }} padding={{ right: "17px" }}>
                                {Object.keys(data["future"]).sort((a, b) => a === "OpenVMS" ? -1 : 0).map((key, idx) => (
                                    <Flex key={`futurePackage-${idx}`} direction="column" gap={{ bottom: 8 }} padding={{ top: "12px" }} style={{ borderTop: idx > 0 && "1px solid var(--sp-light-grey-blue-color)"}}>
                                        <Title h6>{key}</Title>
                                        {data["future"][key].map(pkg => (
                                            <Flex key={pkg.key} flex={1} gap={{ right: 8 }} padding={{ left: "12px"}}>
                                                <span>&mdash;</span>
                                                <Flex flex={1} align="baseline" justify="space-between" gap={{ right: 12 }}>
                                                    <Flex direction="column" gap={{ bottom: 4 }}>
                                                        <Flex align="center" gap={{ right: 8 }}>
                                                            <Title>{pkg.fixVersion?.substring(pkg.fixVersion.search(/\sV\d/i) + 1)}</Title>
                                                            {Array.isArray(pkg.sysarchs) && pkg.sysarchs.map(sysarch => <FilledLabel key={pkg.key+sysarch}>{sysarch}</FilledLabel>)}
                                                        </Flex>
                                                        <Paragraph>{pkg.description}</Paragraph>
                                                        {Array.isArray(pkg.products) && pkg.products.length
                                                            ? <Flex direction="column">
                                                                {pkg.products.map(product => (
                                                                    <Flex key={product.key} align="center" gap={{ right: 8 }}>
                                                                        <Badge color="var(--sp-text-color)" size={5} />
                                                                        <Paragraph>{product.summary}</Paragraph>
                                                                        {product.roadmapDesc &&
                                                                        <FormTooltip
                                                                            visible
                                                                            text={Array.isArray(product.roadmapDesc[0])
                                                                                ? <Flex direction="column">
                                                                                    {product.roadmapDesc.map((line, idx) => (
                                                                                        <span key={`roadmap--desc-line-${idx}`}>{line}</span>
                                                                                    ))}
                                                                                </Flex>
                                                                                : product.roadmapDesc}>
                                                                            <InfoIcon size={18} color="var(--sp-label-color)" />
                                                                        </FormTooltip>}
                                                                    </Flex>
                                                                ))}
                                                            </Flex>
                                                            : null}
                                                    </Flex>
                                                    <span><FilledLabel textTransform="capitalize" fill="var(--sp-success-light-color)" color="#FFF">{pkg.releaseDate ? formatDate(pkg.releaseDate, "MMMM YYYY") : "TBD"}</FilledLabel></span>
                                                </Flex>
                                            </Flex>
                                        ))}
                                    </Flex>
                                ))}
                            </Flex>
                        : <Flex flex={1} justify="center">
                            <Label bold color="#D7E3F7" size="32px">No upcoming releases</Label>
                        </Flex>}
                    </Scrollbar>
                </TableContainer>
            </Flex>
        </Flex>
    );
};

export { MainDashboardContent };